import { useEffect } from 'react'
import {
	CheckIcon,
	SparklesIcon,
	XMarkIcon,
	ArrowRightIcon,
	ShieldCheckIcon,
} from '@heroicons/react/24/solid'
import { PLANS } from '../../config/plans'

function PlanCard({ plan, onSelect, loading }) {
	return (
		<div className="px-6 pb-2">
			<div className="flex items-center gap-3 mb-3">
				<div className="w-10 h-10 rounded-full bg-sky-500/15 ring-1 ring-sky-400/20 flex items-center justify-center shrink-0">
					<SparklesIcon className="w-5 h-5 text-sky-300" />
				</div>
				<h3 className="text-2xl font-semibold text-white">{plan.name}</h3>
				{plan.badge && (
					<span className="ml-1 text-xs font-semibold tracking-wider uppercase text-sky-300 bg-sky-500/10 ring-1 ring-sky-400/20 px-2.5 py-1 rounded-full">
						{plan.badge}
					</span>
				)}
			</div>
			{plan.tagline && (
				<p className="text-base text-gray-400 mb-7">{plan.tagline}</p>
			)}

			<div className="mb-10">
				<div className="flex items-baseline gap-2 mb-1.5">
					<span className="text-gray-400 text-3xl">$</span>
					<span className="text-6xl font-bold text-white tracking-tight">
						{plan.price}
					</span>
					<span className="text-gray-400 text-base ml-1">
						per {plan.interval}
					</span>
				</div>
				{plan.billingNote && (
					<p className="text-sm text-gray-500">{plan.billingNote}</p>
				)}
			</div>

			<div className="space-y-7 mb-9">
				{plan.featureGroups.map(group => (
					<div key={group.title}>
						<h4 className="text-xs font-semibold tracking-wider uppercase text-gray-500 mb-3">
							{group.title}
						</h4>
						<ul className="space-y-3">
							{group.features.map(f => (
								<li
									key={f}
									className="flex items-start gap-3 text-base text-gray-200"
								>
									<CheckIcon className="w-5 h-5 text-sky-400 shrink-0 mt-0.5" />
									<span>{f}</span>
								</li>
							))}
						</ul>
					</div>
				))}
			</div>

			<button
				type="button"
				onClick={() => onSelect(plan.key)}
				disabled={loading}
				className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-slate-900 bg-gradient-to-r from-sky-300 to-blue-400 hover:from-sky-200 hover:to-blue-300 transition-colors disabled:opacity-60 disabled:cursor-wait shadow-lg shadow-sky-500/20"
			>
				{loading ? (
					<>Opening checkout…</>
				) : (
					<>
						{plan.ctaLabel}
						<ArrowRightIcon className="w-4 h-4" />
					</>
				)}
			</button>

			{plan.trustNote && (
				<div className="flex items-center justify-center gap-1.5 mt-5 text-sm text-gray-500">
					<ShieldCheckIcon className="w-4 h-4" />
					<span>{plan.trustNote}</span>
				</div>
			)}
		</div>
	)
}

function UpgradeModal({ isOpen, onClose, onSelectPlan, loadingPlanKey }) {
	useEffect(() => {
		if (!isOpen) return
		const onKey = e => {
			if (e.key === 'Escape') onClose()
		}
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	}, [isOpen, onClose])

	if (!isOpen) return null

	const plans = PLANS

	return (
		<div
			className="fixed inset-0 bg-black/70 backdrop-blur-[1px] flex items-start justify-center z-50 pt-12 p-4 overflow-y-auto"
			onClick={onClose}
		>
			<div
				className={`relative bg-[#0d1520] rounded-3xl w-full ring-1 ring-white/10 shadow-[0_0_30px_rgba(59,130,246,0.15)] ${
					plans.length > 1 ? 'max-w-4xl' : 'max-w-md'
				}`}
				onClick={e => e.stopPropagation()}
			>
				<button
					type="button"
					onClick={onClose}
					className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 z-10"
					aria-label="Close"
				>
					<XMarkIcon className="w-5 h-5" />
				</button>

				{plans.length > 1 && (
					<div className="px-8 pt-8 pb-2">
						<h2 className="text-3xl font-bold text-white">Choose your plan</h2>
					</div>
				)}

				<div
					className={`px-2 pt-8 pb-8 grid gap-6 ${
						plans.length > 1 ? 'sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'
					}`}
				>
					{plans.map(plan => (
						<PlanCard
							key={plan.key}
							plan={plan}
							onSelect={onSelectPlan}
							loading={loadingPlanKey === plan.key}
						/>
					))}
				</div>
			</div>
		</div>
	)
}

export default UpgradeModal
